import React, { useEffect, useMemo, useState } from "react";
import api from "@api";
import { NavLink } from "react-router-dom";
import { useDomain } from "@useDomain";
import "./StandingDefault.css";

function StandingsDefault({ page }) {
  const { slug, domain, organization } = useDomain();

  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState("ALL");
  const [standingsType, setStandingsType] = useState("team");
  const [loading, setLoading] = useState(true);
  const [activeSeason, setActiveSeason] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState("all");
  const [individualLeagues, setIndividualLeagues] = useState([]);
  const content = page?.content || page?.pageSettings?.content || {};

  const filters = content?.filters || {
    showLeagueFilter: true,
    showDivisionFilter: true,
  };

  const visibleHeaders = content?.visibleHeaders || {
    position: true,
    team: true,
    captain: true,
    league: true,
    division: true,
    wins: true,
    losses: true,
    ftPercentage: true,
    total3PM: true,
  };

  const themeMode = organization?.theme_mode || "light";

  const leagueOrder = [
    "Fountain Valley",
    "Tustin Columbus",
    "Irvine Valley College",
  ];

  const dayOrder = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  useEffect(() => {
    const fetchSeasonAndLeagues = async () => {
      try {
        if (!slug && !domain) {
          console.warn("No slug or domain available, skipping fetch");
          return;
        }

        const params = slug ? { slug } : { domain };

        const seasonRes = await api.get(`/api/seasonsv2/public`, { params });
        const allSeasons = seasonRes.data.seasons || [];
        const active = allSeasons.find((season) => season.active);

        if (!active) {
          console.warn("No active season found.");
          return;
        }

        setActiveSeason(active);

        const [divisionsRes, leaguesRes, individualStandingsRes] =
          await Promise.all([
            api.get(`/api/seasonsv2/${active.id}/divisions`, { params }),

            api.get(
              `/api/seasonsv2/${active.id}/leagues-with-teams-public`,
              { params }
            ),

            api.get(
              `/api/seasonsv2/${active.id}/individual-standings-public`,
              { params }
            ),
          ]);

        const sortedDivisions = (divisionsRes.data || []).map((div) => ({
          ...div,
          teams: [...(div.teams || [])].sort(
            (a, b) => (a.position ?? 999) - (b.position ?? 999)
          ),
        }));

        setDivisions(sortedDivisions);

        const leaguesWithTeams = (leaguesRes.data.leagues || []).map((league) => {
          const teams = (league.teams || []).map((team) => ({
            ...team,
            seasonId: active.id,
            seasonName: active.name,
          }));

          return {
            ...league,
            teams,
            seasonName: active.name,
            seasonId: active.id,
          };
        });

        setLeagues(
          leaguesWithTeams.filter(
            (league) => league.league_type !== "individual"
          )
        );

        setIndividualLeagues(
          individualStandingsRes.data.leagues || []
        );
      } catch (error) {
        console.error("Error fetching season and leagues:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSeasonAndLeagues();
  }, [domain, slug]);

  const allLeagues = useMemo(() => {
    return [...leagues, ...individualLeagues];
  }, [leagues, individualLeagues]);

  const hasTeamLeagues = leagues.length > 0;
  const hasIndividualLeagues = individualLeagues.length > 0;

  const hasMixedLeagueTypes =
    hasTeamLeagues && hasIndividualLeagues;
  useEffect(() => {
    if (!hasTeamLeagues && hasIndividualLeagues) {
      setStandingsType("individual");
      setSelectedLeague("ALL");
      setSelectedDivisionId("all");
    }

    if (hasTeamLeagues && !hasIndividualLeagues) {
      setStandingsType("team");
      setSelectedLeague("ALL");
      setSelectedDivisionId("all");
    }
  }, [hasTeamLeagues, hasIndividualLeagues]);
  const sortedLeagues = useMemo(() => {
    const sourceLeagues =
      standingsType === "individual"
        ? individualLeagues
        : leagues;

    return [...sourceLeagues].sort((a, b) => {
      const locationIndexA = leagueOrder.findIndex((loc) =>
        a.name.includes(loc)
      );

      const locationIndexB = leagueOrder.findIndex((loc) =>
        b.name.includes(loc)
      );

      if (locationIndexA !== locationIndexB) {
        if (locationIndexA === -1) return 1;
        if (locationIndexB === -1) return -1;

        return locationIndexA - locationIndexB;
      }

      const dayA =
        dayOrder.find((day) => a.name.includes(day)) || "";

      const dayB =
        dayOrder.find((day) => b.name.includes(day)) || "";

      const dayIndexA = dayOrder.indexOf(dayA);
      const dayIndexB = dayOrder.indexOf(dayB);

      if (dayIndexA === -1 && dayIndexB === -1) {
        return a.name.localeCompare(b.name);
      }

      if (dayIndexA === -1) return 1;
      if (dayIndexB === -1) return -1;

      return dayIndexA - dayIndexB;
    });
  }, [leagues, individualLeagues, standingsType]);

  const selectedLeagueObj =
    selectedLeague && selectedLeague !== "ALL"
      ? sortedLeagues.find(
        (l) => l.id === Number(selectedLeague)
      )
      : null;

  const isIndividualLeague =
    standingsType === "individual";

  let allTeams =
    standingsType !== "team"
      ? []
      : selectedLeague === "ALL"
        ? leagues.flatMap((league) => league.teams || [])
        : selectedLeagueObj?.teams || [];

  if (selectedDivisionId !== "all") {
    const selectedDivision = divisions.find(
      (d) => d.id === Number(selectedDivisionId)
    );

    allTeams = (selectedDivision?.teams || [])
      .map((t) => allTeams.find((team) => team.slt_id === t.slt_id))
      .filter(Boolean);
  }

  const sltToDivisionMap = {};
  divisions.forEach((div) =>
    div.teams.forEach((team) => {
      sltToDivisionMap[team.slt_id] = div.name;
    })
  );

  const isAllLeagues = selectedLeague === "ALL";

  const teams = useMemo(() => {
    return [...allTeams].sort((a, b) => {
      if (selectedLeague === "ALL") {
        if (parseFloat(b.winPercentage) !== parseFloat(a.winPercentage)) {
          return parseFloat(b.winPercentage) - parseFloat(a.winPercentage);
        }
        if (b.wins !== a.wins) return b.wins - a.wins;
        return a.losses - b.losses;
      }

      return a.rank - b.rank;
    });
  }, [allTeams, selectedLeague]);

  const standingsRows = isIndividualLeague
    ? selectedLeagueObj?.players || []
    : teams;

  const visibleFilterCount = [
    filters.showLeagueFilter,
    filters.showDivisionFilter && divisions.length > 0,
  ].filter(Boolean).length;


  const individualPlayers = useMemo(() => {
    if (standingsType !== "individual") return [];

    if (selectedLeague !== "ALL") {
      return selectedLeagueObj?.players || [];
    }

    const playerMap = new Map();

    individualLeagues.forEach((league) => {
      (league.players || []).forEach((entry) => {
        const playerId = entry.player?.id;
        if (playerId == null) return;

        if (!playerMap.has(playerId)) {
          playerMap.set(playerId, {
            ...entry,
            matches: 0,
            wins: 0,
            losses: 0,
          });
        }

        const player = playerMap.get(playerId);
        player.wins += Number(entry.wins || 0);
        player.losses += Number(entry.losses || 0);
      });
    });

    return Array.from(playerMap.values())
      .map((player) => {
        const matches = player.wins + player.losses;

        return {
          ...player,
          matches,
          winPercentage: matches ? player.wins / matches : 0,
        };
      })
      .sort((a, b) =>
        (b.matches > 0) - (a.matches > 0) ||
        b.winPercentage - a.winPercentage ||
        b.wins - a.wins ||
        a.losses - b.losses
      )
      .map((player, index) => ({
        ...player,
        rank: index + 1,
      }));
  }, [
    standingsType,
    selectedLeague,
    selectedLeagueObj,
    individualLeagues,
  ]);




  if (loading) return null;

  return (
    <div className={`public-standings-container standings-theme-${themeMode}`}>
      {visibleFilterCount > 0 && (
        <div className="teamPublic-league-name-container">
          <div className="teamPublic-league-title">
            {hasMixedLeagueTypes && (
              <div className="schedulePublic-type-filter">
                <button
                  type="button"
                  className={
                    standingsType === "team" ? "active" : ""
                  }
                  onClick={() => {
                    setStandingsType("team");
                    setSelectedLeague("ALL");
                    setSelectedDivisionId("all");
                  }}
                >
                  Team
                </button>

                <button
                  type="button"
                  className={
                    standingsType === "individual"
                      ? "active"
                      : ""
                  }
                  onClick={() => {
                    setStandingsType("individual");
                    setSelectedLeague("ALL");
                    setSelectedDivisionId("all");
                  }}
                >
                  Individual
                </button>
              </div>
            )}
            <div className={`team-filter-wrapper filter-count-${visibleFilterCount}`}>
              {filters.showLeagueFilter && (
                <div className="team-filter-item">
                  <select
                    id="league-filter"
                    value={selectedLeague ?? ""}
                    onChange={(e) => setSelectedLeague(e.target.value)}
                  >
                    {selectedLeague === null && (
                      <option value="">Select a league</option>
                    )}
                    <option value="ALL">All Leagues</option>

                    {sortedLeagues.map((league) => (
                      <option
                        key={`${league.id}-${league.seasonId}`}
                        value={league.id}
                      >
                        {league.name} ({league.seasonName})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {filters.showDivisionFilter && divisions.length > 0 && (
                <div className="team-filter-item">
                  <select
                    id="division-filter"
                    value={selectedDivisionId}
                    onChange={(e) => setSelectedDivisionId(e.target.value)}
                  >
                    <option value="all">Tournament Bracket</option>
                    {divisions.map((div) => (
                      <option key={div.id} value={div.id}>
                        {div.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isIndividualLeague ? (
        individualPlayers.length > 0 ? (
          <div className="teamPublic-table-container">
            <table className="teamPublic-table">
              <thead>
                <tr className="standings-head-font">
                  <th>Position</th>
                  <th>Player</th>
                  <th>Matches</th>
                  <th>Wins</th>
                  <th>Losses</th>
                  <th>Win %</th>
                </tr>
              </thead>

              <tbody>
                {individualPlayers.map((entry) => (
                  <tr
                    key={entry.id}
                    className="standings-font"
                  >
                    <td>{entry.rank}</td>

                    <td>
                      <NavLink
                        to={
                          slug
                            ? `/${slug}/players/${entry.player?.id}`
                            : `/players/${entry.player?.id}`
                        }
                      >
                        {entry.player?.firstName}{" "}
                        {entry.player?.lastName}
                      </NavLink>
                    </td>

                    <td>{entry.matches}</td>
                    <td>{entry.wins}</td>
                    <td>{entry.losses}</td>

                    <td>
                      {(Number(entry.winPercentage || 0) * 100).toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="standings-select-note">
            No players available
          </p>
        )
      ) : teams.length > 0 ? (
        <div className="teamPublic-table-container">
          <table className="teamPublic-table">
            <thead>
              <tr className="standings-head-font">
                {isIndividualLeague ? (
                  <>
                    <th>Position</th>
                    <th>Player</th>
                    <th>Matches</th>
                    <th>Wins</th>
                    <th>Losses</th>
                    <th>Sets W-L</th>
                    <th>Games W-L</th>
                    <th>Win %</th>
                  </>
                ) : (
                  <>
                    {visibleHeaders.position && <th>Position</th>}
                    {visibleHeaders.team && <th>Team</th>}
                    {visibleHeaders.captain && <th>Captain</th>}
                    {visibleHeaders.league && isAllLeagues && <th>League</th>}

                        {visibleHeaders.division &&
                          isAllLeagues &&
                          selectedDivisionId === "all" && (
                            <th>Division</th>
                          )}

                    {visibleHeaders.wins && <th>Wins</th>}
                    {visibleHeaders.losses && <th>Losses</th>}
                    {visibleHeaders.ftPercentage && <th>FT%</th>}
                    {visibleHeaders.total3PM && <th>3PM</th>}
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {standingsRows.map((row) => (
                <tr
                  key={`${row.id}-${row.slt_id}`}
                  className="standings-font"
                >
                  {isIndividualLeague ? (
                    <>
                      <td>{row.rank}</td>

                      <td>
                        <NavLink
                          to={
                            slug
                              ? `/${slug}/players/${row.id}`
                              : `/players/${row.id}`
                          }
                        >
                          {row.firstName} {row.lastName}
                        </NavLink>
                      </td>

                      <td>{row.matchesPlayed}</td>
                      <td>{row.wins}</td>
                      <td>{row.losses}</td>
                      <td>{row.setsWon} - {row.setsLost}</td>
                      <td>{row.gamesWon} - {row.gamesLost}</td>
                      <td>{row.winPercentage}%</td>
                    </>
                  ) : (
                    <>
                      {visibleHeaders.position && <td>{row.rank}</td>}

                      {visibleHeaders.team && (
                        <td>
                          <NavLink
                            to={
                              slug
                                ? `/${slug}/teams/${row.id}`
                                : `/teams/${row.id}`
                            }
                          >
                            {row.name}
                          </NavLink>
                        </td>
                      )}

                      {visibleHeaders.captain && (
                        <td>
                          <NavLink
                            to={
                              slug
                                ? `/${slug}/players/${row.captain_id}`
                                : `/players/${row.captain_id}`
                            }
                          >
                            {row.captain}
                          </NavLink>
                        </td>
                      )}

                      {visibleHeaders.league && isAllLeagues && (
                        <td>{row?.leagueName}</td>
                      )}

                        {visibleHeaders.division &&
                          isAllLeagues &&
                          selectedDivisionId === "all" && (
                            <td>{sltToDivisionMap[row.slt_id] || "—"}</td>
                          )}

                      {visibleHeaders.wins && <td>{row.wins}</td>}
                      {visibleHeaders.losses && <td>{row.losses}</td>}
                      {visibleHeaders.ftPercentage && <td>{row.ftPercentage}</td>}
                      {visibleHeaders.total3PM && <td>{row.total3PM}</td>}
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="standings-select-note">Please Select Filter Option</p>
      )}
    </div>
  );
}

export default StandingsDefault;